import React from 'react';
import { Icon } from '@/components/ui/Icon';
import { Avatar } from '@/components/console/Avatar';
import { useT } from '@/i18n/labels';
import type { CustomerProfileDto } from '@/types';

interface ContactSummaryProps {
  customerId: string;
  /**
   * Name from the conversation, used only when the CRM and our identity record have none.
   * LAST in the precedence order on purpose — see the `name` computation below.
   */
  fallbackName?: string;
  profile: CustomerProfileDto | null;
  anonymous: boolean;
  loading: boolean;
  notFound: boolean;
  /**
   * `panel` is the narrow sidebar section: identity essentials only.
   * `page` is the full contact page and additionally renders the tenant's mapped company fields.
   */
  variant?: 'panel' | 'page';
  avatarSize?: number;
}

/**
 * Who the customer is — avatar, name, company, email, phone, and (on the page) the tenant's own
 * mapped company properties.
 *
 * Purely presentational: every piece of state arrives as a prop so the same block can serve the
 * Conversation panel's collapsible contact section and the Contact page without either owning a
 * fetch. {@link useCustomerProfile} is the single fetch path feeding both.
 *
 * Product notes carried over from the panel this was extracted from:
 *  - There is no subscription-plan concept for end customers in BotableX, so the old
 *    Plan / Lifetime-value / Orders tiles were removed (product decision, 2026-07). `profile.tier`
 *    still exists server-side and feeds the AiAgentService prompt context; it is simply not shown.
 *  - The company block renders the TENANT'S mapped properties (CRM v2 WS-6.2) — whatever that tenant
 *    chose to map, not a list this component invented.
 */
export const ContactSummary: React.FC<ContactSummaryProps> = ({
  customerId,
  fallbackName,
  profile,
  anonymous,
  loading,
  notFound,
  variant = 'panel',
  avatarSize = 64,
}) => {
  const { t } = useT();

  // Name precedence: the CRM's own record for this person, then the display name our
  // customer_identities row captured (the From-header name on an inbound email), then whatever the
  // conversation could offer. `fallbackName` is LAST on purpose — for an email conversation the
  // caller's label is the email SUBJECT, which is a fine thread title and plainly wrong as a
  // person's name.
  const name = anonymous
    ? t('ctx_anon_title')
    : (profile?.fullName || fallbackName || '').trim();

  // False ONLY when the server explicitly said so; `undefined` (an older response) is treated as
  // linked so nothing regresses.
  const crmLinked = profile?.crmLinked !== false;

  // Company precedence: the RESOLVED Company record's name first, then the contact's free-text
  // company property. Both read as "the company" to an agent, but only the first came from a
  // record — the second is a string someone once typed and may name a company that does not exist,
  // or spell one that does five different ways. `resolvedCompany` also gates the provenance chip and
  // the mapped-fields group, so the three never disagree.
  const resolvedCompany = (profile?.companyName ?? '').trim();
  const companyLabel = resolvedCompany || (profile?.company ?? '').trim();

  // Only the tenant's OWN mapped properties, and only ones with a value: a mapped property the CRM
  // left blank is noise, not information.
  const companyFields = Object.entries(profile?.companyFields ?? {}).filter(
    ([key, value]) => key.trim() !== '' && (value ?? '').trim() !== '',
  );

  // The contact-side twin, filtered identically: a mapped property the CRM left blank is noise.
  const contextFields = Object.entries(profile?.contextFields ?? {}).filter(
    ([key, value]) => key.trim() !== '' && (value ?? '').trim() !== '',
  );

  return (
    <div data-testid="contact-summary">
      <div className={variant === 'page' ? 'ac-contact-hero' : 'ac-ctx-hero'}>
        <Avatar name={name} seed={customerId} size={avatarSize} className="ac-ctx-av" />
        <div className="ac-ctx-name">{name || t('unidentified')}</div>
        {!anonymous && companyLabel && (
          <div className="ac-ctx-company" data-testid="ctx-company">
            <Icon name="building" size={13} />
            {companyLabel}
            {resolvedCompany && (
              <span
                data-testid="ctx-company-from-record"
                title={t('ctx_company_from_record')}
                className="ac-ctx-provenance"
              >
                CRM
              </span>
            )}
          </div>
        )}
        {!anonymous && profile?.email && <div className="ac-ctx-email">{profile.email}</div>}
        {!anonymous && profile?.phone && <div className="ac-ctx-since">{profile.phone}</div>}
      </div>

      {anonymous ? (
        <div className="ac-ctx-sec" data-testid="ctx-anon" style={{ borderBottom: 'none' }}>
          <div className="ac-ctx-na">{t('ctx_anon_note')}</div>
        </div>
      ) : (
        <>
          {loading && (
            <div className="ac-ctx-sec" data-testid="ctx-loading">
              <div className="ac-ctx-na">{t('ctx_loading')}</div>
            </div>
          )}

          {/*
            "Not in the CRM" is informational, never a reason to render an empty block. Two cases
            reach it:
              • notFound — the lookup failed; we know nothing about this customer at all, and
              • crmLinked === false — the CRM has no contact, but the server still returned a profile
                built from our OWN customer_identities row, so the hero above IS showing their real
                email/phone and name.
            The second is the common case for an inbound email from someone new, and it used to
            render as a blank panel: a 404 threw away the very address the message arrived from.
          */}
          {!loading && (notFound || !crmLinked) && (
            <div className="ac-ctx-sec" data-testid="ctx-none">
              <div className="ac-ctx-na">{t('ctx_none')}</div>
            </div>
          )}

          {/*
            The tenant's MAPPED CONTACT properties — the contact-side twin of the company block
            below. Shown in BOTH variants, unlike that one: these describe the person the agent is
            talking to, which is exactly what this section is for, whereas company details are a
            wider record that earns the full page. Inside the collapsed contact section either way,
            so the panel's default density is unchanged.

            Keys print as the tenant named them, for the same reason the company block gives: they
            are that tenant's own property labels, and prettifying them here would have an agent and
            an admin calling one field two different things.
          */}
          {!loading && contextFields.length > 0 && (
            <div className="ac-ctx-sec" data-testid="ctx-contact-fields">
              {contextFields.map(([key, value]) => (
                <div key={key} className="ac-ctx-kv" data-testid={`ctx-contact-field-${key}`}>
                  <span className="ac-ctx-kv-k">{key}</span>
                  <span className="ac-ctx-kv-v" data-testid={`ctx-contact-field-value-${key}`}>
                    {value}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/*
            The tenant's MAPPED company properties (CRM v2 WS-6.2). Page only — in the narrow panel
            these would crowd out the conversation details the panel now exists to show. Rendered
            only when a company RECORD resolved AND it carried mapped values, so a tenant that mapped
            nothing sees no empty titled box. Keys print as the tenant named them: they are that
            tenant's own property labels, and inventing prettier ones here would mean an agent and an
            admin calling the same field two different things.
          */}
          {variant === 'page' && !loading && resolvedCompany && companyFields.length > 0 && (
            <div className="ac-ctx-sec" data-testid="ctx-company-fields">
              <div className="ac-ctx-sec-title">{t('ctx_company_details')}</div>
              {companyFields.map(([key, value]) => (
                <div key={key} className="ac-ctx-kv" data-testid={`ctx-company-field-${key}`}>
                  <span className="ac-ctx-kv-k">{key}</span>
                  <span className="ac-ctx-kv-v" data-testid={`ctx-company-field-value-${key}`}>
                    {value}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
